import React from 'react';
import { AlertTriangle, Briefcase, Gavel, Target } from 'lucide-react';
import { FilingPanel } from './FilingPanel';

/** Business description, objects of the issue, risk factors and litigation. */
export const FilingDisclosures = ({ overview, industry, objects, risks, litigation, litigationNote }) => (
  <>
    {(overview || industry) && (
      <FilingPanel id="filing-business-heading" title="Business Description" icon={Briefcase}>
        {overview && <p className="text-sm text-slate-600 leading-relaxed">{overview}</p>}
        {industry && (
          <p className={`text-xs text-slate-500 leading-relaxed ${overview ? 'mt-3 pt-3 border-t border-slate-100' : ''}`}>
            <span className="font-bold text-slate-600">Industry: </span>
            {industry}
          </p>
        )}
      </FilingPanel>
    )}

    {objects.length > 0 && (
      <FilingPanel id="filing-objects-heading" title="Objects of the Issue" icon={Target}>
        <ol className="space-y-2.5">
          {objects.map((item, index) => (
            <li key={item.object} className="flex items-start gap-3">
              <span className="w-5 h-5 rounded-md bg-teal-50 text-[#006666] text-[10px] font-extrabold flex items-center justify-center flex-shrink-0 mt-0.5">
                {index + 1}
              </span>
              <span className="flex-1 text-sm text-slate-700 leading-snug">{item.object}</span>
              {item.amount && <span className="text-sm font-bold text-slate-900 tabular-nums whitespace-nowrap">{item.amount}</span>}
            </li>
          ))}
        </ol>
      </FilingPanel>
    )}

    {risks.length > 0 && (
      <FilingPanel id="filing-risks-heading" title="Risk Factors Disclosed" icon={AlertTriangle}>
        <ul className="space-y-2.5">
          {risks.map((risk) => (
            <li key={risk} className="flex items-start gap-2.5 text-sm text-slate-700 leading-snug">
              <AlertTriangle className="w-3.5 h-3.5 mt-1 text-amber-500 flex-shrink-0" aria-hidden="true" />
              {risk}
            </li>
          ))}
        </ul>
      </FilingPanel>
    )}

    {litigation && (
      <FilingPanel id="filing-litigation-heading" title="Outstanding Litigation" icon={Gavel} note={litigationNote}>
        <div className="overflow-x-auto scrollbar-slim">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200/80 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                <th scope="col" className="py-2 pr-3 min-w-[150px]">
                  Proceedings
                </th>
                {litigation.columns.map((column) => (
                  <th key={column.key} scope="col" className="py-2 px-3 text-right whitespace-nowrap">
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-[13px]">
              {litigation.rows.map((row) => (
                <tr key={row.category}>
                  <th scope="row" className="py-2.5 pr-3 font-semibold text-slate-600">
                    {row.category}
                  </th>
                  {row.cells.map((cell, index) => (
                    <td key={litigation.columns[index].key} className="py-2.5 px-3 text-right font-bold text-slate-900 tabular-nums">
                      {cell || <span className="font-normal text-slate-300">—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </FilingPanel>
    )}
  </>
);

export default FilingDisclosures;
